import { useState, useCallback } from "react";
import { apiFetch } from "@/lib/api-fetch";

export type IntentField = "topic" | "instructions" | "message";
export type IntentAction = "fill" | "fill_and_generate";

export interface IntentResult {
  field: IntentField;
  suggestedAction: IntentAction;
  displayText: string;
}

export interface UseIntentDetectionReturn {
  isDetecting: boolean;
  detectedIntent: IntentResult | null;
  detectIntent: (transcript: string) => Promise<IntentResult | null>;
  clearIntent: () => void;
}

export function useIntentDetection(options: {
  sessionId: string;
  baseUrl: string;
}): UseIntentDetectionReturn {
  const { sessionId, baseUrl } = options;
  const [isDetecting, setIsDetecting] = useState(false);
  const [detectedIntent, setDetectedIntent] = useState<IntentResult | null>(null);

  const detectIntent = useCallback(
    async (transcript: string): Promise<IntentResult | null> => {
      if (!transcript.trim()) return null;
      setIsDetecting(true);
      try {
        const res = await apiFetch(`${baseUrl}/api/voice/detect-intent`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "x-session-id": sessionId,
          },
          body: JSON.stringify({ transcript }),
        });
        if (!res.ok) {
          return null;
        }
        const data = (await res.json()) as IntentResult;
        setDetectedIntent(data);
        return data;
      } catch {
        return null;
      } finally {
        setIsDetecting(false);
      }
    },
    [sessionId, baseUrl]
  );

  const clearIntent = useCallback(() => {
    setDetectedIntent(null);
  }, []);

  return { isDetecting, detectedIntent, detectIntent, clearIntent };
}
